import React from 'react';
import { Button, Grid2, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { EntityService } from 'core/api/services/lookUp';

import { LookUpContract, DateRangePickerWithInput, TIME_ZONES } from '@utriper/core-ui';

import { useTraffic } from 'core/contexts/traffic';

const ReportFilter = () => {
    const { filters, updateFilter, loading, getDashboard } = useTraffic();

    const { t: translate, i18n } = useTranslation();

    const handleDateRangeChange = ({ startDate, endDate }) => {
        if (!startDate) {
            toast.info(translate('reports.filters.start_date_required'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        const minAllowedDate = DateTime.utc(2026, 1, 1, 0, 0);

        // Validation 1: No dates earlier than 01/01/2026
        if (startDate.toMillis() < minAllowedDate.toMillis()) {
            toast.error(translate('reports.filters.date_too_early'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        // Validation 2: The end date is later than the start date.
        if (endDate && endDate.toMillis() < startDate.toMillis()) {
            toast.error(translate('reports.filters.date_too_early'), { position: 'top-right', theme: 'colored' });
            return false;
        }

        // Validation 3: No range bigger than 365 days
        if (endDate) {
            const daysDifference = Math.ceil(endDate.diff(startDate, 'days')?.days);
            if (daysDifference > 365) {
                toast.error(translate('reports.filters.range_too_large'), { position: 'top-right', theme: 'colored' });
                return false;
            }
        }

        updateFilter('startDate', startDate);
        updateFilter('endDate', endDate);
    };

    const isLoading = loading;

    const handleFetchReports = async () => {
        if (!filters?.startDate) {
            toast.info(translate('reports.filters.start_date_required'), { position: 'top-right', theme: 'colored' });
            return;
        }
        await getDashboard();
    };

    return (
        <Grid2 container spacing={1} width="full" size={12} marginBottom={2} flexWrap={{ xs: 'wrap', lg: 'nowrap' }}>
            <DateRangePickerWithInput
                value={{ startDate: filters.startDate, endDate: filters.endDate }}
                style={{
                    width: '100%',
                    minWidth: '280px',
                }}
                onChange={handleDateRangeChange}
                useUTC
            />

            <Select style={{ width: '100%' }} variant="outlined" labelId="timezone" id="timezone" value={filters.timezone} onChange={(e) => updateFilter('timezone', e?.target?.value)}>
                {TIME_ZONES.map((tz) => (
                    <MenuItem key={tz.id} style={{ color: '#131313' }} value={tz.id}>
                        {tz.name}
                    </MenuItem>
                ))}
            </Select>
            <Grid2 sx={{ width: '100%', minWidth: { xs: '280px', lg: 'auto' } }}>
                <LookUpContract label={translate('reports.filters.contract')} value={filters.contractIds} onChange={(val) => updateFilter('contractIds', Array.isArray(val) ? val.map((v) => v) : [val])} entityService={EntityService} disabled={isLoading} multiSelect />
            </Grid2>

            <Button variant="outlined" color="primary" onClick={handleFetchReports} disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 500, minWidth: 'fit-content' }}>
                {translate('reports.filters.get')}
            </Button>
        </Grid2>
    );
};

export default ReportFilter;
